/**
 * One working-location change at a time per day (`yyyy-MM-dd`).
 *
 * A day's reservations are read before anything is written, so without this a
 * second pick made during that round trip would race the first: whichever
 * answered last would decide the day, which is not necessarily the one the
 * person chose last.
 *
 * A day is held two ways, and they are counted apart because they overlap. A
 * change may be *running* on it — reading its reservations, or writing the
 * answer somebody gave. Its prompt may be *on screen*, waiting on the person,
 * which is part of the change and not the end of it. Only one prompt shows at
 * a time, so opening a second takes the screen from the first, and the day
 * that one asked about is let go: nothing else ever would, and a day left held
 * could never be picked again. But letting go of the prompt must not let go of
 * the day, because by then the person may have answered it and the writing may
 * still be under way — which is why `prompt` cannot simply drop the day, and
 * why `answered` names the day it is answering for rather than clearing
 * whichever prompt happens to be up.
 */
export interface DayChangeGuard {
  /** Whether the day is held, by running work or by a prompt of its own. */
  isBusy: (date: string) => boolean;
  /** Work on the day starts. */
  begin: (date: string) => void;
  /** That work is over. A prompt of the day's own goes on holding it. */
  end: (date: string) => void;
  /** This day's prompt is the one on screen now, in place of any other. */
  prompt: (date: string) => void;
  /** That day's prompt was answered; no-op once another has taken over. */
  answered: (date: string) => void;
  /** Whatever prompt is on screen was closed without an answer. */
  dismissPrompt: () => void;
}

export const createDayChangeGuard = (): DayChangeGuard => {
  const working = new Set<string>();
  let prompted: string | null = null;

  return {
    isBusy: (date) => working.has(date) || prompted === date,
    begin: (date) => {
      working.add(date);
    },
    end: (date) => {
      working.delete(date);
    },
    prompt: (date) => {
      prompted = date;
    },
    answered: (date) => {
      if (prompted === date) {
        prompted = null;
      }
    },
    dismissPrompt: () => {
      prompted = null;
    }
  };
};
